import {useCallback, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {toast} from 'react-toastify';

import {BusinessDocumentType} from '@afriex/core/domain/enums';
import {mapBusinessUserKYCToDocumentType} from '@afriex/core/utils';

import {merchantService} from '@/services/merchants';
import type {
  AfriexBusinessUser,
  AfriexBusinessUserMeta,
} from '@/types/AdminBusinessUser';
import {BusinessKYCDocumentType} from '@/types/BusinessKYCDocumentType';
import {getErrorMessage} from '@/utils/errorMessage';
import {logRejection} from '@/utils/logRejection';

import type {KYCUpdatePayload, UserLoadingFlags} from './types';

const useMerchantKYC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const userId = params?.userId || '';
  const [loading, setIsLoading] = useState(false);
  const [loadingKYC, setIsLoadingKYC] = useState(false);
  const [userLoadingFlags, setUserLoadingFlags] = useState<UserLoadingFlags>({
    isSecurityFlagged: false,
    isDeactivated: false,
    isVerified: false,
    isOwner: false,
    isDeleted: false,
    hasSeededData: false,
  });
  const [IdType, setIdType] = useState<string | null>(null);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [userDetails, setUserDetails] = useState<AfriexBusinessUser>(
    {} as AfriexBusinessUser,
  );

  const [fileUrls, setFileUrls] = useState<{url: string; name: string}[]>([]);

  const fetchUserDetails = useCallback(async (): Promise<void> => {
    try {
      if (!userId) {
        return;
      }
      setIsLoading(true);
      const response = await merchantService.getBusinessUser(userId);
      if (response?.person) {
        setUserDetails(response.person);
        setBusinessId(response.person.businessId || null);
        const possibleIds: BusinessKYCDocumentType[] = [
          BusinessKYCDocumentType.NationalID,
          BusinessKYCDocumentType.BVN,
          BusinessKYCDocumentType.DriverLicense,
          BusinessKYCDocumentType.Passport,
        ];

        const kycData = response.person.meta?.kyc?.data;
        const idType = possibleIds.find((key) => kycData?.[key]);

        setIdType(idType || null);
      }
    } catch (err) {
      const errorMessage = getErrorMessage(err, '');
      toast.error('Failed to fetch business details:' + errorMessage);
      setIsLoading(false);
    }
  }, [userId]);

  const updateUserKYC = useCallback(
    async (kyc: KYCUpdatePayload): Promise<void> => {
      try {
        if (!userId || !businessId) {
          return;
        }
        setIsLoadingKYC(true);
        await merchantService.updateUserKYC(businessId, userId, kyc);
        toast.success('KYC Documents uploaded successfully');
        setTimeout(() => {
          // The delta overload of `navigate` is typed `void | Promise<void>`
          // (it returns a promise on the data router, nothing otherwise), so
          // normalize before attaching the handler. A same-route reload has
          // nothing to await it; log a failure rather than leave the rejection
          // unhandled.
          Promise.resolve(navigate(0)).catch(
            logRejection('Failed to reload after KYC update'),
          );
        }, 1500);
      } catch (err) {
        const errorMessage = getErrorMessage(err, '');
        toast.error('Failed to upload KYC Documents:' + errorMessage);
      } finally {
        setIsLoadingKYC(false);
      }
    },
    [userId, businessId],
  );

  const requestAdditionalDocuments = useCallback(
    async (documents: BusinessKYCDocumentType[]): Promise<void> => {
      if (!userId || !businessId) {
        throw new Error('Missing business or user id');
      }
      await merchantService.requestBusinessUserKYCDocuments(
        businessId,
        userId,
        documents,
      );
      setTimeout(() => {
        Promise.resolve(navigate(0)).catch(
          logRejection('Failed to reload after requesting KYC documents'),
        );
      }, 1500);
    },
    [userId, businessId],
  );

  const fetchFileUrls = useCallback(() => {
    try {
      if (!userId) {
        return;
      }

      const kyc = userDetails?.meta?.kyc;
      const docs = kyc?.docs ?? [];
      const data = kyc?.data ?? {};

      // Docs URLs — server already resolves these to presigned URLs
      const docsUrls: {url: string; name: string}[] = [];
      for (const doc of docs) {
        const source = doc?.media?.source;
        // An empty source is skipped, as the original truthy filter did.
        if (typeof source === 'string' && source) {
          docsUrls.push({
            url: source,
            name: doc.idType?.replace(/_/g, ' ') || 'Document',
          });
        }
      }

      // Data file/photo fields (server already resolves these to presigned
      // URLs). Derive the file/photo key set from the shared core mapping so
      // admin stays in lockstep with the server as document types are added.
      const dataFileKeys = (
        Object.entries(mapBusinessUserKYCToDocumentType) as [
          BusinessKYCDocumentType,
          BusinessDocumentType,
        ][]
      )
        .filter(
          ([, docType]) =>
            docType === BusinessDocumentType.File ||
            docType === BusinessDocumentType.Photo,
        )
        .map(([key]) => key);

      const dataUrls = dataFileKeys
        .filter((key) => {
          const value = data[key];
          return typeof value === 'string' && value.trim() !== '';
        })
        .map((key) => ({
          url: data[key] as string,
          name: key.replace(/_/g, ' '),
        }));

      setFileUrls([...docsUrls, ...dataUrls]);
    } catch (err) {
      console.error('Error fetching files:', err);
    } finally {
      setIsLoading(false);
    }
  }, [userId, userDetails]);

  const updateUserFlag = useCallback(
    async (flag?: Partial<AfriexBusinessUserMeta['flags']>): Promise<void> => {
      if (!userId || !businessId || !flag) {
        return;
      }

      const keys = Object.keys(flag) as (keyof typeof userLoadingFlags)[];
      const flagKey = keys[0];
      if (!flagKey) {
        return;
      }

      try {
        setUserLoadingFlags((prev) => ({...prev, [flagKey]: true}));
        await merchantService.updateUserFlag(businessId, userId, flag);
        toast.success('User flag updated successfully');
        // Same as updateUserKYC: `setTimeout` wants a `=> void` callback and
        // `navigate` returns a promise, so handle the rejection here.
        setTimeout(() => {
          Promise.resolve(navigate(0)).catch(
            logRejection('Failed to reload after user flag update'),
          );
        }, 1500);
      } catch (err) {
        toast.error('Failed to update user flag: ' + getErrorMessage(err, ''));
      } finally {
        setUserLoadingFlags((prev) => ({...prev, [flagKey]: false}));
      }
    },
    [userId, businessId],
  );

  useEffect(() => {
    if (!userId) {
      return;
    }
    // Both fetchers handle their own failures (toast / console), so these only
    // fire if that catch or the finally itself throws. Log, do not swallow.
    fetchUserDetails().catch(
      logRejection('Failed to fetch business user details'),
    );
  }, [userId]);

  useEffect(() => {
    if (!userId || Object.keys(userDetails).length < 1) {
      return;
    }
    // fetchFileUrls is synchronous and try/catches internally, so nothing
    // here can throw.
    fetchFileUrls();
  }, [userId, userDetails]);

  return {
    loading,
    userDetails,
    userLoadingFlags,
    updateUserKYC,
    requestAdditionalDocuments,
    userId,
    businessId,
    fileUrls,
    updateUserFlag,
    IdType,
    loadingKYC,
  };
};

export default useMerchantKYC;
